import React, { useState, useEffect, useCallback } from 'react';
import { BCS_LEVEL, TAGLEVELS, TAGTYPE } from '../utils/Constants';
import { useApiCaller } from '../utils/ApiUtilities';
import { getTagUrl } from '../utils/UrlUtilities';
import { constructTagPayload } from '../utils/PayloadUtils';
import CloseButton from './ui/CloseButton';

// --- Mocked Dependencies for Standalone Component ---
// In a real project, these would be imported from their respective files.

const Input = ({ type, placeholder, value, onChange, className, required, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy }) => (
    <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`form-control ${className || ''}`}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
    />
);

// --- End Mocked Dependencies ---


export default function TagAddForm({ onCancel, onSave }) {
    const [tagName, setTagName] = useState('');
    const [level, setLevel] = useState('');
    const [type, setType] = useState('');
    const [subject, setSubject] = useState(''); // New state for subject
    const [availableSubjects, setAvailableSubjects] = useState([]); // State to hold fetched subjects
    const [bcsTermYear, setBcsTermYear] = useState('');

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const CallApi = useApiCaller(); // Using the mocked API caller
    const fetchSubjects = async () => {
        if (level && level !== "SELECT") {
            try {
                const url = getTagUrl({ level: level, tag_type: "SUBJECT" });
                const response = await CallApi({
                    url: url,
                    method: 'GET',
                    token: true,
                    payload: null
                });
                if (response.success) {
                    const uniqueSubjects = [...new Set(response.data.tags.map(tag => tag.name))];
                    setAvailableSubjects(uniqueSubjects);
                } else {
                    setAvailableSubjects([]);
                    console.error("Failed to fetch subjects:", response.message);
                }
            } catch (err) {
                setAvailableSubjects([]);
                console.error("Error fetching subjects:", err);
            }
        } else {
            setAvailableSubjects([]); // Clear subjects if no level is selected
        }
    };

    // Effect to fetch subjects when level changes and type is 'TOPIC'
    useEffect(() => {
        if (type === "TOPIC") { // Only fetch subjects if type is TOPIC
            fetchSubjects();
        } else {
            setSubject(''); // Clear subject if type is not TOPIC
            setAvailableSubjects([]);
        }
    }, [level, type]); // Re-run when level or type changes

    // BCS_TERM only ever applies to level=BCS — force the level when admin
    // picks this type so the constraint can't be violated by hand.
    useEffect(() => {
        if (type === "BCS_TERM" && level !== BCS_LEVEL) {
            setLevel(BCS_LEVEL);
        }
        if (type !== "BCS_TERM") {
            setBcsTermYear('');
        }
    }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async () => {
        setError('');

        // Validation for fields
        if (!tagName || !level || level === "SELECT" || !type || type === "SELECT") {
            setError('Please fill all required fields (Tag Name, Level, Type).');
            return;
        }

        if (type === "TOPIC" && (!subject || subject === "SELECT")) {
            setError('Please select a Subject for TOPIC type tags.');
            return;
        }

        setIsSaving(true);
        try {
            let finalTagName = tagName;
            if (type === "TOPIC") {
                // Concatenate subject and tag name for TOPIC type
                finalTagName = `${subject}-${tagName}`;
            }

            const metadata = type === "BCS_TERM" && bcsTermYear.trim()
                ? { year: bcsTermYear.trim() }
                : null;
            // For TOPIC tags, also send the first-class subject field (authoritative link).
            const tagSubject = type === "TOPIC" ? subject : undefined;
            const newTagPayload = constructTagPayload(finalTagName, level, type, metadata, undefined, tagSubject);
            console.log("post payload : ", newTagPayload);
            const postTagResponse = await CallApi({
                url: getTagUrl(),
                method: 'POST',
                payload: newTagPayload,
                token: true,
            });

            if (postTagResponse.success) {
                setTimeout(() => {
                    onSave(postTagResponse.data); // Calls the onSave from parent, which re-fetches tags
                }, 500);
            } else {
                setError(postTagResponse.message || 'Failed to add tag.');
                console.error('Add Tag Error:', postTagResponse);
            }
        } catch (err) {
            setError('An error occurred while adding the tag.');
            console.error('Add Tag Error in catch block:', err);
        } finally {
            setIsSaving(false);
        }
    };

    // Determine if Save button should be enabled
    let isFormValid = tagName && level && level !== "SELECT" && type && type !== "SELECT";
    if (type === "TOPIC") {
        isFormValid = isFormValid && subject && subject !== "SELECT";
    }

    return (
        // Bootstrap Modal Structure
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-md" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title fs-5 fw-bold text-dark">Add New Tag</h5>
                        <CloseButton onClick={onCancel} />
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger mb-3" role="alert">{error}</div>}

                        <form onSubmit={(e) => e.preventDefault()}>
                            <div className="mb-3">
                                <label htmlFor="tagName" className="form-label text-gray-700">Tag Name <span className="text-danger">*</span></label>
                                <Input
                                    type="text"
                                    id="tagName"
                                    value={tagName}
                                    onChange={(e) => setTagName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="tagLevel" className="form-label text-gray-700">Tag Level <span className="text-danger">*</span></label>
                                <select
                                    id="tagLevel"
                                    value={level}
                                    onChange={(e) => setLevel(e.target.value)}
                                    className="form-select"
                                    required
                                    disabled={type === "BCS_TERM"}
                                    title={type === "BCS_TERM" ? "BCS terms only apply to the BCS level" : ""}
                                >
                                    <option value="" disabled>SELECT</option>
                                    {TAGLEVELS.map((item) => (
                                        item.submenu ? (
                                            <optgroup key={item.value} label={item.label}>
                                                {item.submenu.map((sub) => (
                                                    <option key={sub.value} value={sub.value}>
                                                        {sub.label}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ) : (
                                            <option key={item.value} value={item.value} disabled={item.value === "" && item.label === "SELECT"}>
                                                {item.label}
                                            </option>
                                        )
                                    ))}
                                </select>
                                {type === "BCS_TERM" && (
                                    <p className="text-muted small mt-1">BCS terms only apply to the BCS level.</p>
                                )}
                            </div>
                            <div className="mb-3">
                                <label htmlFor="tagType" className="form-label text-gray-700">Tag Type <span className="text-danger">*</span></label>
                                <select
                                    id="tagType"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    className="form-select"
                                    required
                                >
                                    <option value="" disabled>SELECT</option>
                                    {TAGTYPE.map((tagTypeOption) => (
                                        <option key={tagTypeOption} value={tagTypeOption} disabled={tagTypeOption === "SELECT"}>
                                            {tagTypeOption}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* BCS_TERM-only optional year metadata. Stored under
                              * metadata.year and surfaced everywhere a term is picked
                              * so the year field can auto-fill. */}
                            {type === "BCS_TERM" && (
                                <div className="mb-3">
                                    <label htmlFor="bcsTermYear" className="form-label text-gray-700">Year (optional)</label>
                                    <Input
                                        type="text"
                                        id="bcsTermYear"
                                        placeholder="e.g. 2021"
                                        value={bcsTermYear}
                                        onChange={(e) => setBcsTermYear(e.target.value)}
                                    />
                                    <p className="text-muted small mt-1">Filled into the question form when this term is chosen.</p>
                                </div>
                            )}

                            {/* New: Conditional Subject Dropdown */}
                            {type === "TOPIC" && (
                                <div className="mb-3">
                                    <label htmlFor="tagSubject" className="form-label text-gray-700">Subject <span className="text-danger">*</span></label>
                                    <select
                                        id="tagSubject"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="form-select"
                                        required
                                        disabled={!level || availableSubjects.length === 0} // Disable if no level selected or no subjects fetched
                                    >
                                        <option value="" disabled>SELECT</option>
                                        {availableSubjects.length > 0 ? (
                                            availableSubjects.map((subName) => (
                                                <option key={subName} value={subName}>
                                                    {subName}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="" disabled>No subjects available for this level</option>
                                        )}
                                    </select>
                                    {!level && <p className="text-muted small mt-1">Select a level first to see subjects.</p>}
                                    {level && availableSubjects.length === 0 && <p className="text-muted small mt-1">No subjects found for {level}.</p>}
                                </div>
                            )}
                        </form>
                    </div>
                    <div className="modal-footer d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onCancel}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-success"
                            onClick={handleSave}
                            disabled={!isFormValid || isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save Tag'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
